"use client";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
  ReferenceLine,
  Cell,
} from "recharts";
import { BarChart2, ShoppingCart, ChevronDown, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { useDashboardPanel } from "@/hooks/useDashboardPanel";
import { Button } from "@/components/ui/button";

const tabs = [
  { icon: <ShoppingCart size={20} />, label: "Orders", key: "orders" },
  { icon: <BarChart2 size={20} />, label: "Wholesalers", key: "wholesalers" },
];

import { Card, CardContent } from "@/components/ui/card";

export default function EarningReport() {
  const [selectedTab, setSelectedTab] = useState(0); // Start with Orders (index 0)
  const [selectedBarIndex, setSelectedBarIndex] = useState(2); // Start with March selected
  const { refreshPanel, isRefreshing } = useDashboardPanel("earnings");
  const reduxChartData = useSelector(
    (state: RootState) => state.dashboard?.chartData ?? null,
  );
  const dashboardSnapshot = useSelector(
    (state: RootState) => state.dashboard?.snapshot ?? null,
  );
  const chartData =
    reduxChartData && Array.isArray(reduxChartData) ? reduxChartData : [];
  const reportYear =
    dashboardSnapshot?.earnings_report?.year || new Date().getFullYear();

  const handleBarClick = (data: unknown, index: number) => {
    const barData = data as Record<string, unknown> & { name?: string };
    if (!barData.name) return;
    setSelectedBarIndex(index);
    console.log(
      `Clicked on ${barData.name} with value:`,
      barData[tabs[selectedTab]?.key || "orders"],
    );
  };

  return (
    <div className="w-full">
      <div className="mb-4 flex items-center justify-between flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold text-foreground mb-1">
            Earning Reports
          </h2>
          <p className="text-xs text-muted-foreground">
            Yearly Earnings Overview for {reportYear}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={refreshPanel}
            disabled={isRefreshing}
            className="h-8 w-8 p-0"
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
          </Button>
          <button className="text-sm bg-transparent text-gray-600 dark:text-gray-300 px-4 py-1 rounded-xl flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border border-border">
            {reportYear} <ChevronDown size={16} className="ml-1" />
          </button>
        </div>
      </div>

      <Card className="w-full border-border/60 bg-card/85 backdrop-blur-xl shadow-theme-sm">
        <CardContent className="p-4">
          <div className="mb-4 flex gap-3 flex-shrink-0">
            {tabs.map((tab, index) => (
              <button
                key={index}
                className={`flex flex-col items-center justify-center w-24 h-20 rounded-2xl transition-colors
              ${
                index === selectedTab
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                  : "bg-gray-50 dark:bg-auth-background text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
              }
            `}
                onClick={() => setSelectedTab(index)}
              >
                <span className="mb-1">{tab.icon}</span>
                {tab.label && (
                  <span className="text-sm font-medium">{tab.label}</span>
                )}
              </button>
            ))}
          </div>

          <div className="relative overflow-hidden h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                barCategoryGap="20%"
                margin={{ top: 30, right: 10, left: -25, bottom: 10 }}
              >
                <defs>
                  <clipPath id="chart-clip">
                    <rect x="0" y="0" width="100%" height="100%" />
                  </clipPath>
                </defs>
                <XAxis
                  dataKey="name"
                  tick={{ fill: "var(--chart-text-color)", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  padding={{ left: 13, right: 10 }}
                />

                <YAxis
                  tick={{ fill: "var(--chart-text-color)", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${v}k`}
                  padding={{ top: 0, bottom: 10 }}
                  domain={[0, 50]}
                />
                <ReferenceLine
                  y={0}
                  stroke="hsl(var(--primary))"
                  strokeWidth={1}
                />
                <Bar
                  dataKey={tabs[selectedTab]?.key || "orders"}
                  radius={[4, 4, 0, 0]}
                  onClick={handleBarClick}
                  style={{ cursor: "pointer" }}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        index === selectedBarIndex
                          ? "hsl(var(--primary))"
                          : "var(--chart-bar-color)"
                      }
                    />
                  ))}
                  <LabelList
                    dataKey={tabs[selectedTab]?.key || "orders"}
                    position="top"
                    formatter={(v) => `${v}k`}
                    fill="var(--chart-text-color)"
                    fontSize={12}
                    fontWeight={400}
                    offset={8}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Overlay to hide any borders */}
            <div className="absolute inset-0 pointer-events-none">
              <div
                className="w-full h-full bg-transparent border-0 outline-0"
                style={{
                  background:
                    "linear-gradient(to right, transparent 0%, transparent 100%)",
                  mixBlendMode: "normal",
                }}
              />
            </div>
          </div>

          <style>{`
            :root {
              --chart-text-color: #6b7280;
              --chart-bar-color: #e0e7ff;
            }

            .dark {
              --chart-text-color: #ffffff;
              --chart-bar-color: #2a2a3a;
            }

            .recharts-wrapper {
              border: none !important;
              outline: none !important;
              box-shadow: none !important;
            }
            .recharts-surface {
              border: none !important;
              outline: none !important;
              box-shadow: none !important;
            }
            .recharts-cartesian-grid {
              border: none !important;
              outline: none !important;
            }
            .recharts-cartesian-grid-horizontal line,
            .recharts-cartesian-grid-vertical line {
              stroke: none !important;
            }
            .recharts-rectangle {
              cursor: pointer;
            }
            svg {
              border: none !important;
              outline: none !important;
              box-shadow: none !important;
            }
          `}</style>
        </CardContent>
      </Card>
    </div>
  );
}
